'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { z } from '@/lib/validation/zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useId, useMemo, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'

import { FormErrorBanner } from '@/components/site/forms/form-error-banner'
import { Button } from '@/components/ui/button'
import { ErrorMessage, Field, Label } from '@/components/ui/fieldset'
import { Input } from '@/components/ui/input'
import { analytics } from '@/lib/analytics/posthog'

import { formatMessage } from '@/components/site/forms/form-helpers'
import { Select } from '@/components/ui/select'
import { WorkspaceInput } from '@/components/ui/workspace-input'
import { appDomain } from '@/config/env.public'
import { identifyByEmail } from '@/lib/analytics/identity'
import { applyApiErrors, createSlug, LIMITS, requestJson } from './register-helpers'
import type { RegisterCopy, RegisterData, RegisterUi } from './register-types'

type RegisterCompanyResponse = Partial<RegisterData>

type CompanyStepProps = {
  copy: RegisterCopy['company']
  ui: RegisterUi
  defaultValues: RegisterData
  onNext: (data: Partial<RegisterData>) => void
  onPrevious: (data: Partial<RegisterData>) => void
}

export function RegisterCompanyStep({ copy, ui, defaultValues, onNext, onPrevious }: CompanyStepProps) {
  const companyId = useId()
  const workspaceId = useId()
  const companySizeId = useId()
  const referralSourceId = useId()
  const referralSourceExplanationId = useId()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [domainTouched, setDomainTouched] = useState(false)

  const schema = useMemo(
    () =>
      z.object({
        company: z
          .string()
          .trim()
          .min(1, ui.validation.companyRequired)
          .min(LIMITS.companyMin, formatMessage(ui.validation.companyMin, { min: LIMITS.companyMin })),
        domain: z
          .string()
          .trim()
          .min(1, ui.validation.workspaceRequired)
          .min(LIMITS.domainMin, formatMessage(ui.validation.workspaceMin, { min: LIMITS.domainMin }))
          .max(LIMITS.domainMax, formatMessage(ui.validation.workspaceMax, { max: LIMITS.domainMax }))
          .regex(/^[a-z-]+$/, ui.validation.workspacePattern)
          .refine((value) => !value.startsWith('-') && !value.endsWith('-'), ui.validation.workspacePattern),
        companySize: z.string().optional(),
        referralSource: z.string().optional(),
        referralSourceExplanation: z.string().optional(),
      }),
    [
      ui.validation.companyRequired,
      ui.validation.companyMin,
      ui.validation.workspaceRequired,
      ui.validation.workspaceMin,
      ui.validation.workspaceMax,
      ui.validation.workspacePattern,
    ],
  )

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setFocus,
    setError,
    control,
    formState: { errors, isSubmitting, isSubmitted },
  } = useForm<{
    company: string
    domain: string
    companySize?: string
    referralSource?: string
    referralSourceExplanation?: string
  }>({
    defaultValues: {
      company: defaultValues.company || '',
      domain: defaultValues.domain || '',
      companySize: defaultValues.companySize || '',
      referralSource: defaultValues.referralSource || '',
      referralSourceExplanation: defaultValues.referralSourceExplanation || '',
    },
    resolver: zodResolver(schema),
  })

  const referralSource = useWatch({ control, name: 'referralSource' })

  useEffect(() => {
    setFocus('company')
  }, [setFocus])

  // React Hook Form keeps an unmounted input's value, and the wizard feeds every step's values
  // back in as defaults — so someone who typed an explanation under "Anders" and then picked
  // another option submitted both, and the manager prefers the explanation. Their visible answer
  // would be Google while the stored one said their accountant.
  const freeTextOption = copy.options.referralSources[copy.options.referralSources.length - 1]
  useEffect(() => {
    if (referralSource !== freeTextOption) {
      setValue('referralSourceExplanation', '')
    }
  }, [freeTextOption, referralSource, setValue])

  const onCompanyChange = (value: string) => {
    setValue('company', value, { shouldDirty: true, shouldValidate: isSubmitted })
    if (domainTouched) {
      return
    }
    const newDomain = createSlug(value || '')
    if (newDomain !== getValues('domain')) {
      setValue('domain', newDomain, { shouldDirty: false, shouldValidate: isSubmitted })
    }
  }

  const onSubmit = handleSubmit(async (values) => {
    setErrorMessage(null)

    const normalizedDomain = createSlug(values.domain || '')
    if (values.domain !== normalizedDomain) {
      setError('domain', { message: ui.validation.workspacePattern })
      return
    }

    try {
      const data = await requestJson<RegisterCompanyResponse>('register/company/check', {
        method: 'POST',
        body: JSON.stringify({ ...defaultValues, ...values }),
      })

      // Only categorical (non free-text, non-PII) fields go on the event.
      analytics.capture('signup_company_submitted', {
        company_size: values.companySize || null,
        referral_source: values.referralSource || null,
      })

      // Also set them on the person so customer value can be segmented by company size / channel.
      if (defaultValues.email) {
        identifyByEmail(defaultValues.email, {
          company_size: values.companySize || undefined,
          referral_source: values.referralSource || undefined,
        })
      }

      onNext({ ...values, ...(data ?? {}) })
    } catch (error) {
      applyApiErrors(error, setError, setErrorMessage, ui.validation.submitFailed)
    }
  })

  const onPreviousClick = () => {
    onPrevious(getValues())
  }

  const labelClass = 'text-mist-900 dark:text-mist-100'
  const buttonColor = 'dark/zinc'

  return (
    <form className="space-y-5" noValidate onSubmit={onSubmit}>
      {errorMessage && <FormErrorBanner className="font-medium">{errorMessage}</FormErrorBanner>}

      <Field>
        <Label htmlFor={companyId} className={labelClass} required>
          {ui.fields.company}
        </Label>
        <Input
          id={companyId}
          type="text"
          autoComplete="organization"
          placeholder={ui.placeholders.signup.company.company}
          {...register('company', {
            onChange: (event) => onCompanyChange(event.target.value),
          })}
          invalid={Boolean(errors.company)}
          aria-invalid={Boolean(errors.company)}
          aria-describedby={errors.company ? `${companyId}-error` : undefined}
          disabled={isSubmitting}
        />
        {errors.company?.message && <ErrorMessage id={`${companyId}-error`}>{errors.company.message}</ErrorMessage>}
      </Field>

      <Field>
        <Label htmlFor={workspaceId} className={labelClass} required>
          {ui.fields.workspace}
        </Label>
        <Controller
          name="domain"
          control={control}
          render={({ field }) => (
            <WorkspaceInput
              id={workspaceId}
              name={field.name}
              autoComplete="off"
              placeholder={`${ui.placeholders.login.workspace}.${appDomain}`}
              domain={appDomain}
              value={field.value ?? ''}
              onBlur={field.onBlur}
              onChange={(event) => {
                setDomainTouched(true)
                field.onChange(event)
              }}
              invalid={Boolean(errors.domain)}
              aria-invalid={Boolean(errors.domain)}
              aria-describedby={errors.domain ? `${workspaceId}-error` : undefined}
              disabled={isSubmitting}
            />
          )}
        />
        {errors.domain?.message && <ErrorMessage id={`${workspaceId}-error`}>{errors.domain.message}</ErrorMessage>}
      </Field>

      <Field>
        <Label htmlFor={companySizeId} className={labelClass}>
          {ui.fields.companySize}
        </Label>
        <Select id={companySizeId} {...register('companySize')} disabled={isSubmitting} defaultValue="">
          <option value="">{ui.placeholders.signup.company.companySize}</option>
          {copy.options.companySizes.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        <Label htmlFor={referralSourceId} className={labelClass}>
          {ui.fields.referralSource}
        </Label>
        <Select id={referralSourceId} {...register('referralSource')} disabled={isSubmitting} defaultValue="">
          <option value="">{ui.placeholders.signup.company.referralSource}</option>
          {copy.options.referralSources.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      </Field>

      {referralSource === copy.options.referralSources[copy.options.referralSources.length - 1] && (
        <Field>
          <Label htmlFor={referralSourceExplanationId} className={labelClass}>
            {ui.fields.referralSourceExplanation}
          </Label>
          <Input
            id={referralSourceExplanationId}
            type="text"
            placeholder={ui.placeholders.signup.company.referralSourceExplanation}
            {...register('referralSourceExplanation')}
            disabled={isSubmitting}
          />
        </Field>
      )}

      <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-between">
        <Button type="submit" size="lg" color={buttonColor} disabled={isSubmitting}>
          {ui.actions.next}
        </Button>
        <Button
          type="button"
          size="lg"
          plain
          className="!text-mist-700 hover:!text-mist-950 dark:!text-mist-300 dark:hover:!text-white"
          onClick={onPreviousClick}
          disabled={isSubmitting}
        >
          {ui.actions.back}
        </Button>
      </div>
    </form>
  )
}
